import {
    Fragment, useEffect, useId, useLayoutEffect, useMemo, useRef, useState,
    type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type ReactNode,
} from 'react'
import { ArrowLeftRight, ChevronLeft, ChevronRight } from 'lucide-react'
import SortIcon from './EmployeeSortIcon'
import TransactionItemsRow from './EmployeeTransactionItemsRow'
import { useColumnDrag } from './useColumnDrag'
import { useEmployeeTransactions } from './useEmployeeTransactions'
import { trapTab } from '../../lib/focusTrap'
import { formatEmployeeValue, type EmployeePerformanceQueryContext, type EmployeePerformanceRow, type SortDir } from '../../lib/employeePerformance'
import {
    DEFAULT_TRANSACTION_SORT,
    defaultTransactionColumnOrder,
    describeEmployeeTransactionsError,
    formatTransactionTime,
    getTransactionColumn,
    isDefaultTransactionSort,
    loadTransactionColumnOrder,
    moveTransactionColumn,
    nextTransactionSort,
    saveTransactionColumnOrder,
    sortEmployeeTransactions,
    transactionChannelName,
    type EmployeeTransaction,
    type TransactionColumnKey,
    type TransactionSort,
} from '../../lib/employeeTransactions'

// ── Employee Performance widget: an employee's receipts ─────────────────
//
// The drill-down. Opened from an employee in the widget, it lists the receipts behind their row for
// exactly the dashboard's current view — the widget's own period and filters, sent as they are, plus
// the employee. Newest first and capped; the totals row always covers every receipt, so it matches
// the widget's row even when the list is cut short, and the dialog says when it is.
//
// Every heading sorts the receipts (press again to reverse) for as long as the dialog is open, and
// the columns go in whatever order the viewer likes: drag a heading (useColumnDrag), or use the
// arrows in the Arrange strip — the way from the keyboard and on a touchscreen. The order is kept in
// this browser for every employee's receipts; the sort is not. The totals row follows the order:
// each figure stays under its column, and the Total label spans the first run of columns without one.
//
// Each receipt opens in place to show the items on the sale (EmployeeTransactionItemsRow): from its
// button in the leading column, or a click anywhere on its row — though a click, or a double or triple
// click, that selects some text there leaves it as it was. That column is the table's own, not
// one of the columns — it never sorts or moves, and the Arrange strip leaves it out. Any number of
// receipts can be open. They stay open, each under its receipt, through sorts, column moves and
// reloads, and the dialog forgets them when it shuts. A receipt's items are asked for only when it is
// first opened.
//
// Portalled to <body> by the panel: the grid positions each widget with a CSS transform, which would
// trap a position: fixed dialog inside the card. Follows the modal rule — the dimmed backdrop does
// nothing, and the ✕ and Close shut it. It holds no edits, so Escape shuts it as well. Tab stays inside.

export interface EmployeeTransactionsModalProps {
    employee: Pick<EmployeePerformanceRow, 'employeeId' | 'name' | 'departed' | 'manualGrants'>
    /** The widget's own period, filters and page query key. */
    query: EmployeePerformanceQueryContext
    /** The widget's wording for the period: 'Today', 'September 2026', … */
    periodLabel: string
    /** The store selector's wording: 'Calgary', 'All Stores', … */
    storeLabel: string
    onClose: () => void
}

type MoveDir = 'left' | 'right'

/** react-grid-layout starts a widget drag on these; events from a portal still bubble through React. */
const stopGrid = (e: { stopPropagation: () => void }) => e.stopPropagation()

const money = (n: number) => formatEmployeeValue('currencyCents', n)
const whole = (n: number) => formatEmployeeValue('int', n)
const receipts = (n: number) => `${whole(n)} ${n === 1 ? 'receipt' : 'receipts'}`

const ariaSortFor = (dir: SortDir) => (dir === 'asc' ? 'ascending' : 'descending')

/** Class names, leaving out the ones that don't apply; undefined when none do. */
const cx = (...names: (string | false | null | undefined)[]) => names.filter(Boolean).join(' ') || undefined

export default function EmployeeTransactionsModal({ employee, query, periodLabel, storeLabel, onClose }: EmployeeTransactionsModalProps) {
    const baseId = useId()
    const arrangeId = `${baseId}-arrange`
    const dialogRef = useRef<HTMLDivElement>(null)
    const headingRowRef = useRef<HTMLTableRowElement>(null)
    const arrangeListRef = useRef<HTMLOListElement>(null)
    const { data, error } = useEmployeeTransactions(query, employee.employeeId)
    const { channel, includeReturns, includeMultibuys } = query.filters
    const ctx = { includeReturns }
    const name = employee.name.trim() || 'Unknown employee'
    const grants = employee.manualGrants ?? 0

    const [order, setOrder] = useState<TransactionColumnKey[]>(loadTransactionColumnOrder)
    const [sort, setSort] = useState<TransactionSort>(DEFAULT_TRANSACTION_SORT)
    const [arranging, setArranging] = useState(false)
    const [announcement, setAnnouncement] = useState('')
    /** The receipts open to show their items, by id. Held here, so sorts, moves and reloads leave them open; gone with the dialog. */
    const [openReceipts, setOpenReceipts] = useState<ReadonlySet<string>>(() => new Set())
    /** After an arrow move in the Arrange strip, focus stays with the column that moved (an arrow can go disabled at an end). */
    const refocus = useRef<{ key: TransactionColumnKey; dir: MoveDir } | null>(null)

    const columns = useMemo(() => order.map(getTransactionColumn), [order])
    // Always sorted from the server's list, so receipts that tie stay newest first.
    const rows = useMemo(
        () => (data ? sortEmployeeTransactions(data.transactions, sort.key, sort.dir) : []),
        [data, sort.key, sort.dir],
    )
    const labelOf = (key: TransactionColumnKey) => getTransactionColumn(key).label(ctx)

    const rearrange = (next: TransactionColumnKey[], message: string) => {
        setOrder(next)
        saveTransactionColumnOrder(next)
        setAnnouncement(message)
    }

    const move = (from: number, to: number, via?: MoveDir) => {
        if (from < 0 || from >= order.length || to < 0 || to >= order.length || from === to) return
        const key = order[from]
        if (via) refocus.current = { key, dir: via }
        rearrange(moveTransactionColumn(order, from, to), `${labelOf(key)} moved to position ${to + 1} of ${order.length}.`)
    }

    const resetOrder = () => rearrange(defaultTransactionColumnOrder(), 'Columns back in their original order.')

    const sortBy = (key: TransactionColumnKey) => {
        const next = nextTransactionSort(sort, key)
        setSort(next)
        setAnnouncement(`Sorted by ${labelOf(next.key)}, ${getTransactionColumn(next.key).dirWords[next.dir]}.`)
    }

    /** The receipt a click on its row has just opened or closed — the first click of a double-click, if one follows. */
    const toggledByRow = useRef<string | null>(null)

    /** Opens a receipt's items under it, or closes them. Focus stays where it is. */
    const toggleReceipt = (transactionId: string) => setOpenReceipts(current => {
        const next = new Set(current)
        if (!next.delete(transactionId)) next.add(transactionId)
        return next
    })

    /**
     * Anywhere on a receipt's row opens or closes its items — unless the click ends a text selection.
     * A double or triple click selects a word or the row, but its first click lands before anything is
     * selected: that one toggles like any click, the clicks after it leave the receipt alone, and the
     * double-click takes the first one back once it has selected something (onReceiptDoubleClick).
     */
    const onReceiptClick = (e: ReactMouseEvent, transactionId: string) => {
        if (e.detail > 1) return
        toggledByRow.current = null
        if (window.getSelection?.()?.toString()) return
        toggledByRow.current = transactionId
        toggleReceipt(transactionId)
    }

    /** A double-click that selected some text on a receipt's row was a selection: its first click's toggle is undone. */
    const onReceiptDoubleClick = (transactionId: string) => {
        const toggled = toggledByRow.current === transactionId
        toggledByRow.current = null
        if (toggled && window.getSelection?.()?.toString()) toggleReceipt(transactionId)
    }

    const { drag, headingProps } = useColumnDrag(headingRowRef, (from, to) => move(from, to))

    useEffect(() => { dialogRef.current?.focus() }, [])

    useLayoutEffect(() => {
        const want = refocus.current
        if (!want) return
        refocus.current = null
        const item = arrangeListRef.current?.querySelector(`[data-col="${want.key}"]`)
        const same = item?.querySelector<HTMLButtonElement>(`[data-move="${want.dir}"]`)
        const other = item?.querySelector<HTMLButtonElement>(`[data-move="${want.dir === 'left' ? 'right' : 'left'}"]`)
        ;(same && !same.disabled ? same : other)?.focus()
    })

    const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            e.stopPropagation()
            onClose()
            return
        }
        trapTab(e, dialogRef.current)
    }

    // The view, spelled out, so it can be checked against the dashboard behind the backdrop.
    const view = [
        periodLabel,
        storeLabel,
        channel === 'instore' ? 'In-Store' : channel === 'online' ? 'Online' : null,
        includeReturns ? null : 'Gross (returns left out)',
        includeMultibuys ? null : 'Multibuys left out',
    ].filter(Boolean).join(' · ')

    let body: ReactNode
    if (error) {
        body = <div className="emp-perf-state" role="status">{describeEmployeeTransactionsError(error)}</div>
    } else if (!data) {
        body = <div className="emp-perf-state" role="status">Loading transactions…</div>
    } else if (data.transactions.length === 0) {
        body = <div className="emp-perf-state" role="status">No receipts for {name} in this view.</div>
    } else {
        const { totals } = data
        const last = columns.length - 1

        /** Drag feedback on a cell spanning columns start–end: tinted while it is the column moving, ruled on the edge it would land against. */
        const dragClass = (start: number, end = start) => drag
            ? cx(
                start === end && drag.from === start && 'is-moving',
                drag.slot === start && 'drop-before',
                drag.slot === last + 1 && end === last && 'drop-after',
            )
            : undefined

        /** Names a receipt's items, for its button and their list: by its number, or by its time when it has none. */
        const itemsLabel = (t: EmployeeTransaction) => (t.receiptNumber
            ? `Items on receipt #${t.receiptNumber}`
            : `Items on the receipt at ${formatTransactionTime(t.time, data.startDate, data.endDate)}`)

        const cell = (key: TransactionColumnKey, t: EmployeeTransaction, extra: string | undefined): ReactNode => {
            switch (key) {
                case 'time':
                    // The row header wherever the column sits: a screen reader reads it out with each figure in the row.
                    return (
                        <th key={key} scope="row" className={cx('emp-txn-time', extra)}>
                            <time dateTime={t.time} title={t.time.replace('T', ' ')}>
                                {formatTransactionTime(t.time, data.startDate, data.endDate)}
                            </time>
                        </th>
                    )
                case 'store':
                    return <td key={key} className={extra}>{t.storeName ?? '—'}</td>
                case 'receipt':
                    return (
                        <td key={key} className={cx('emp-txn-receipt', extra)} title={t.till != null ? `Till ${t.till}` : undefined}>
                            {t.receiptNumber ? `#${t.receiptNumber}` : '—'}
                        </td>
                    )
                case 'channel':
                    return <td key={key} className={extra}>{transactionChannelName(t.channel)}</td>
                case 'units':
                    return <td key={key} className={cx('emp-perf-num', extra)}>{whole(t.units)}</td>
                case 'sales':
                    return <td key={key} className={cx('emp-perf-num', extra)}>{money(t.netSales)}</td>
                case 'discounts':
                    return <td key={key} className={cx('emp-perf-num', extra)}>{money(t.discounts)}</td>
                case 'bucks':
                    return <td key={key} className={cx('emp-perf-num', extra)}>{money(t.bucksCredited)}</td>
                case 'return':
                    return <td key={key} className={extra}>{t.hasReturn && <span className="emp-perf-tag emp-txn-return">Return</span>}</td>
            }
        }

        // The totals row: each figure under its column, the Total label across the first run of
        // columns without one, and any later run left blank. The disclosure column has no figure
        // either: the label takes it in when the label starts the row, and it is left blank when a
        // figure does.
        const foot: ReactNode[] = []
        if (columns[0].total) foot.push(<td key="lead" className="emp-txn-lead" />)
        let labelled = false
        for (let start = 0; start <= last;) {
            const def = columns[start]
            if (def.total) {
                const value = totals[def.total]
                foot.push(
                    <td key={def.key} className={cx('emp-perf-num', dragClass(start))}>
                        {def.total === 'units' ? whole(value) : money(value)}
                    </td>,
                )
                start += 1
                continue
            }
            let end = start
            while (end < last && !columns[end + 1].total) end += 1
            // Only ever the label's run: none comes before the first.
            const takesLead = start === 0
            const width = end - start + 1 + (takesLead ? 1 : 0)
            const colSpan = width > 1 ? width : undefined
            if (labelled) {
                foot.push(<td key={def.key} colSpan={colSpan} className={dragClass(start, end)} />)
            } else {
                labelled = true
                foot.push(
                    <th key={def.key} scope="row" colSpan={colSpan} className={cx(takesLead && 'emp-txn-total-lead', dragClass(start, end))}>
                        <span className="emp-perf-total-label">Total</span>
                        <span className="emp-perf-sub">
                            {data.truncated ? `All ${receipts(totals.transactions)}` : receipts(totals.transactions)}
                        </span>
                    </th>,
                )
            }
            start = end + 1
        }

        body = (
            <>
                <div className="emp-txn-toolbar">
                    {data.truncated && (
                        <p className="emp-txn-note" role="note">
                            Showing the newest {whole(data.transactions.length)} of {receipts(totals.transactions)}
                            {isDefaultTransactionSort(sort) ? '.' : ', so the sort covers only those.'} The totals row covers all of them.
                        </p>
                    )}
                    <button
                        type="button"
                        className="emp-perf-action emp-txn-arrange-toggle"
                        aria-expanded={arranging}
                        aria-controls={arranging ? arrangeId : undefined}
                        onClick={() => setArranging(open => !open)}
                    >
                        <ArrowLeftRight size={13} aria-hidden="true" />
                        Arrange columns
                    </button>
                </div>

                {arranging && (
                    <div id={arrangeId} className="emp-txn-arrange" role="group" aria-labelledby={`${arrangeId}-title`}>
                        <span id={`${arrangeId}-title`} className="emp-txn-arrange-title">Column order, left to right</span>
                        <ol ref={arrangeListRef} className="emp-txn-arrange-list">
                            {columns.map((def, i) => {
                                const label = def.label(ctx)
                                return (
                                    <li key={def.key} data-col={def.key} className="emp-txn-arrange-item">
                                        <button type="button" className="emp-txn-move" data-move="left" aria-label={`Move ${label} left`}
                                            title="Move left" disabled={i === 0} onClick={() => move(i, i - 1, 'left')}>
                                            <ChevronLeft size={14} aria-hidden="true" />
                                        </button>
                                        <span className="emp-txn-arrange-name">{label}</span>
                                        <button type="button" className="emp-txn-move" data-move="right" aria-label={`Move ${label} right`}
                                            title="Move right" disabled={i === last} onClick={() => move(i, i + 1, 'right')}>
                                            <ChevronRight size={14} aria-hidden="true" />
                                        </button>
                                    </li>
                                )
                            })}
                        </ol>
                        <div className="emp-txn-arrange-foot">
                            <p className="emp-txn-arrange-hint">
                                You can also drag a column by its heading. This browser keeps your order for every employee’s receipts.
                            </p>
                            <button type="button" className="emp-perf-action" onClick={resetOrder}>Reset order</button>
                        </div>
                    </div>
                )}

                <div className="emp-txn-scroll" role="region" aria-label={`Receipts rung by ${name}`} tabIndex={0}>
                    <table className={cx('emp-perf-table emp-txn-table', drag && 'is-dragging')}>
                        <thead>
                            <tr ref={headingRowRef}>
                                {/* The disclosure column: the table's own, not one of the columns. It never sorts or
                                    moves, so it has no sort button and no data-col — the drag counts only cells with one. */}
                                <th scope="col" className="emp-txn-lead"><span className="sr-only">Items</span></th>
                                {columns.map((def, i) => {
                                    const active = sort.key === def.key
                                    return (
                                        <th
                                            key={def.key}
                                            scope="col"
                                            data-col={def.key}
                                            className={cx(def.numeric && 'emp-perf-num', dragClass(i))}
                                            aria-sort={active ? ariaSortFor(sort.dir) : undefined}
                                            title={def.description?.(ctx)}
                                            {...headingProps(i)}
                                        >
                                            <button type="button" className="emp-perf-sort" onClick={() => sortBy(def.key)}>
                                                <span>{def.label(ctx)}</span>
                                                <SortIcon active={active} dir={sort.dir} />
                                            </button>
                                        </th>
                                    )
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(t => {
                                const open = openReceipts.has(t.transactionId)
                                const itemsId = `${baseId}-items-${t.transactionId}`
                                const label = itemsLabel(t)
                                return (
                                    // A receipt and, while it is open, its items directly beneath it — whatever the sort.
                                    <Fragment key={t.transactionId}>
                                        <tr
                                            className={cx('is-expandable', open && 'is-open')}
                                            onClick={e => onReceiptClick(e, t.transactionId)}
                                            onDoubleClick={() => onReceiptDoubleClick(t.transactionId)}
                                        >
                                            <td className="emp-txn-lead">
                                                <button
                                                    type="button"
                                                    className="emp-txn-disclose"
                                                    aria-expanded={open}
                                                    aria-controls={open ? itemsId : undefined}
                                                    aria-label={label}
                                                    // The click is the button's alone: the row's would toggle it straight back. Every
                                                    // press toggles, and a double one selects nothing for the row to take back.
                                                    onClick={e => {
                                                        e.stopPropagation()
                                                        toggledByRow.current = null
                                                        toggleReceipt(t.transactionId)
                                                    }}
                                                >
                                                    <ChevronRight size={14} aria-hidden="true" className="emp-txn-chevron" />
                                                </button>
                                            </td>
                                            {columns.map((def, i) => cell(def.key, t, dragClass(i)))}
                                        </tr>
                                        {open && (
                                            <TransactionItemsRow
                                                id={itemsId}
                                                query={query}
                                                employeeId={employee.employeeId}
                                                transactionId={t.transactionId}
                                                label={label}
                                                colSpan={columns.length + 1}
                                            />
                                        )}
                                    </Fragment>
                                )
                            })}
                        </tbody>
                        <tfoot>
                            <tr>{foot}</tr>
                        </tfoot>
                    </table>
                </div>
            </>
        )
    }

    return (
        <div className="modal-backdrop" onMouseDown={stopGrid} onPointerDown={stopGrid} onTouchStart={stopGrid}>
            <div
                ref={dialogRef}
                className="modal emp-txn"
                role="dialog"
                aria-modal="true"
                aria-label={`Transactions — ${name}`}
                aria-describedby={`${baseId}-view`}
                tabIndex={-1}
                onKeyDown={onKeyDown}
            >
                <div className="modal-header">
                    <div className="emp-txn-heading">
                        <h2>
                            <span>{name}</span>
                            {employee.departed && <span className="emp-perf-tag">former</span>}
                        </h2>
                        <p id={`${baseId}-view`} className="emp-txn-scope">Transactions · {view}</p>
                    </div>
                    <button type="button" className="modal-close" aria-label="Close" onClick={onClose}>✕</button>
                </div>

                <div className="modal-body emp-txn-body">
                    {body}
                    {grants > 0 && (
                        <p className="emp-txn-note">Manual grants ({money(grants)}) aren’t sales, so they aren’t listed here.</p>
                    )}
                    <div className="sr-only" aria-live="polite">{announcement}</div>
                </div>

                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    )
}
